import {
  Component,
  DestroyRef,
  ElementRef,
  computed,
  effect,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { DatePipe, DecimalPipe, Location, formatDate } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Title } from '@angular/platform-browser';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import {
  catchError,
  filter,
  interval,
  map,
  merge,
  of,
  startWith,
  Subject,
  switchMap,
  timer,
} from 'rxjs';
import { AppsService } from './apps.service';
import { AuthService } from './auth.service';
import { Banner } from './banner';
import { environment } from '../environments/environment';
import { FilterBar, FilterChip, FilterSelect, ViewOption } from './filter-bar';
import { formatAge } from './format-age';
import { HealthCard } from './health-card';
import { HealthRow } from './health-row';
import { AppHealth, HealthRules, HealthStatus, ServiceBusEntity } from './models';
import { Overview } from './overview';
import {
  Summary,
  applicationInsightsSource,
  attentionItems,
  serviceBusSource,
  summarizeApps,
  summarizeOverall,
  summarizeServiceBus,
} from './overview-model';
import { ServiceBus } from './service-bus';
import { ServiceBusViewState } from './service-bus-state';
import { healthTone } from './tone';
import {
  FILTERS,
  SORTS,
  SortKey,
  ViewMode,
  byName,
  byRate,
  byWorst,
  readViewFromUrl,
  writeViewToUrl,
} from './view-state';

const REFRESH_INTERVAL_MS = 60_000;

// A reading older than this many refresh intervals is flagged as old data.
const STALE_AFTER_REFRESHES = 3;

// How often the "2 min ago" texts are brought up to date.
const CLOCK_INTERVAL_MS = 30_000;

const PAGE_TITLE = 'Solvind Dashboard';

type ProblemKind = 'unreachable' | 'rejected' | 'forbidden' | 'signin';

const PROBLEM_TITLES: Record<ProblemKind, string> = {
  unreachable: 'The health data could not be loaded.',
  rejected: 'Your sign-in was not accepted.',
  forbidden: 'You do not have access to this dashboard.',
  signin: 'Signing in failed.',
};

const SKELETON_CARDS = [0, 1, 2, 3, 4, 5];

const VIEW_OPTIONS: ViewOption[] = [
  { value: 'cards', label: 'Cards' },
  { value: 'list', label: 'List' },
];

type DashboardTab = 'overview' | 'application-insights' | 'service-bus';

@Component({
  selector: 'app-root',
  imports: [
    Banner,
    DatePipe,
    DecimalPipe,
    FilterBar,
    HealthCard,
    HealthRow,
    Overview,
    RouterLink,
    ServiceBus,
  ],
  templateUrl: './app.html',
  styleUrl: './app.css',
})
export class App {
  private readonly appsService = inject(AppsService);
  protected readonly auth = inject(AuthService);
  private readonly title = inject(Title);
  private readonly destroyRef = inject(DestroyRef);
  private readonly manualRefresh = new Subject<void>();
  private readonly initialView = readViewFromUrl();
  private readonly location = inject(Location);
  private readonly serviceBusView = inject(ServiceBusViewState);
  // The tab is the address: /application-insights and /service-bus are tabs of their own, everything else the overview.
  private readonly url = toSignal(
    inject(Router).events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => event.urlAfterRedirects),
    ),
    { initialValue: this.location.path() },
  );
  protected readonly tab = computed<DashboardTab>(() => {
    const path = this.url().split(/[?#]/)[0].replace(/\/$/, '');
    return path === '/service-bus'
      ? 'service-bus'
      : path === '/application-insights'
        ? 'application-insights'
        : 'overview';
  });

  protected readonly apps = signal<AppHealth[]>([]);
  protected readonly loading = signal(true);
  protected readonly error = signal<string | null>(null);
  protected readonly errorKind = signal<ProblemKind>('unreachable');
  protected readonly errorTitle = computed(() => PROBLEM_TITLES[this.errorKind()]);
  // Signing in failed or the API refused the reader: nothing of the dashboard is shown, only what to do next.
  protected readonly blocked = computed(
    () => this.error() !== null && this.errorKind() !== 'unreachable',
  );
  protected readonly lastUpdated = signal<Date | null>(null);
  protected readonly rules = signal<HealthRules | null>(null);
  // The explanation of the health rules belongs to the Application Insights tab only.
  protected readonly healthRules = computed(() =>
    this.tab() === 'application-insights' ? this.rules() : null,
  );
  protected readonly pageTitle = computed(() => {
    switch (this.tab()) {
      case 'service-bus':
        return 'Service Bus dead letters';
      case 'application-insights':
        return 'Application Insights health';
      default:
        return 'Overview';
    }
  });

  protected readonly serviceBus = signal<ServiceBusEntity[]>([]);
  protected readonly serviceBusLoading = signal(true);
  protected readonly serviceBusError = signal<string | null>(null);
  protected readonly serviceBusUpdated = signal<Date | null>(null);
  private readonly serviceBusRefreshSeconds = signal(REFRESH_INTERVAL_MS / 1000);
  protected readonly sort = signal(this.initialView.sort);
  protected readonly query = signal(this.initialView.query);
  // Nothing selected means every health type is shown.
  protected readonly selected = signal<ReadonlySet<HealthStatus>>(this.initialView.selected);
  protected readonly view = signal(this.initialView.view);
  // An empty name means every resource group.
  protected readonly group = signal(this.initialView.group);

  protected readonly environmentName = environment.name;
  protected readonly skeletons = SKELETON_CARDS;

  // The page's clock, so every "2 min ago" moves together.
  protected readonly now = signal(new Date());
  protected readonly staleAfterMs = computed(
    () =>
      (this.rules()?.refreshSeconds ?? REFRESH_INTERVAL_MS / 1000) * 1000 * STALE_AFTER_REFRESHES,
  );
  protected readonly serviceBusStaleAfterMs = computed(
    () => this.serviceBusRefreshSeconds() * 1000 * STALE_AFTER_REFRESHES,
  );

  // The refresh button and the update time belong to the tab that is open; the overview covers both sources.
  protected readonly refreshing = computed(() => {
    switch (this.tab()) {
      case 'service-bus':
        return this.serviceBusLoading();
      case 'application-insights':
        return this.loading();
      default:
        return this.loading() || this.serviceBusLoading();
    }
  });
  // On the overview the oldest reading is shown, since that is how old the picture can be.
  protected readonly shownUpdated = computed(() => {
    switch (this.tab()) {
      case 'service-bus':
        return this.serviceBusUpdated();
      case 'application-insights':
        return this.lastUpdated();
      default: {
        const times = [this.lastUpdated(), this.serviceBusUpdated()].filter(
          (time): time is Date => time !== null,
        );
        return times.length === 0
          ? null
          : new Date(Math.min(...times.map((time) => time.getTime())));
      }
    }
  });
  protected readonly shownUpdatedAgo = computed(() => {
    const updated = this.shownUpdated();
    return updated === null ? '' : formatAge(updated, this.now());
  });

  private readonly topbar = viewChild<ElementRef<HTMLElement>>('topbar');
  private readonly headerVisible = signal(true);

  // The overall picture over every card, whatever the filter: is anything wrong?
  protected readonly summary = computed(() => summarizeApps(this.apps()));

  protected readonly unhealthyCount = computed(
    () => this.apps().filter((app) => app.status === 'Unhealthy').length,
  );
  // Queues and topics with at least one dead letter, wherever the reader is looking.
  protected readonly deadLetteredCount = computed(
    () => this.serviceBus().filter((entity) => entity.status === 'DeadLettered').length,
  );

  protected readonly serviceBusSummary = computed(() => summarizeServiceBus(this.serviceBus()));

  // The summary under the title is about the tab that is open; nothing is shown before there are cards.
  protected readonly headerSummary = computed<Summary | null>(() => {
    const apps = this.apps().length > 0 ? this.summary() : null;
    const serviceBus = this.serviceBus().length > 0 ? this.serviceBusSummary() : null;
    switch (this.tab()) {
      case 'service-bus':
        return serviceBus;
      case 'application-insights':
        return apps;
      default:
        return summarizeOverall(apps, serviceBus);
    }
  });

  // One tile per source; the clusters will be one more entry here.
  protected readonly sources = computed(() => [
    applicationInsightsSource(this.apps(), {
      loading: this.loading(),
      error: this.error(),
      updated: this.lastUpdated(),
    }),
    serviceBusSource(this.serviceBus(), {
      loading: this.serviceBusLoading(),
      error: this.serviceBusError(),
      updated: this.serviceBusUpdated(),
    }),
  ]);
  protected readonly attention = computed(() => attentionItems(this.apps(), this.serviceBus()));

  protected readonly hasFilter = computed(
    () => this.selected().size > 0 || this.query().trim() !== '' || this.group() !== '',
  );

  // Shown in the toolbar once the header summary has scrolled out of view.
  protected readonly compactSummary = computed(() =>
    this.headerVisible() || this.apps().length === 0
      ? null
      : { tone: this.summary().tone, headline: this.summary().headline },
  );

  protected readonly viewOptions = VIEW_OPTIONS;

  protected readonly selects = computed<FilterSelect[]>(() => {
    const counts = new Map<string, number>();
    for (const app of this.apps()) {
      if (app.resourceGroup) {
        counts.set(app.resourceGroup, (counts.get(app.resourceGroup) ?? 0) + 1);
      }
    }
    // A group from the URL stays selectable even when it has no cards (any more).
    if (this.group() !== '' && !counts.has(this.group())) {
      counts.set(this.group(), 0);
    }
    const groups = [...counts]
      .sort(([a], [b]) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
      .map(([name, count]) => ({ value: name, label: `${name} (${count})` }));
    const sort: FilterSelect = {
      id: 'sort',
      label: 'Sort by',
      className: 'sort',
      value: this.sort(),
      options: SORTS.map(({ value, label }) => ({ value, label })),
    };
    return groups.length > 1
      ? [
          {
            id: 'group',
            label: 'Resource group',
            value: this.group(),
            options: [{ value: '', label: 'All' }, ...groups],
          },
          sort,
        ]
      : [sort];
  });

  // The group and the search narrow the cards first, so the counts on the health buttons follow them.
  private readonly searched = computed(() => {
    const group = this.group();
    const query = this.query().trim().toLowerCase();
    const inGroup =
      group === '' ? this.apps() : this.apps().filter((app) => app.resourceGroup === group);
    return query === ''
      ? inGroup
      : inGroup.filter((app) =>
          [app.name, app.roleName, app.appId].some((value) => value?.toLowerCase().includes(query)),
        );
  });

  protected readonly matchCount = computed(() => this.searched().length);

  protected readonly chips = computed<FilterChip[]>(() => {
    const searched = this.searched();
    const selected = this.selected();
    return FILTERS.flatMap(({ status, label, always }) => {
      const count = searched.filter((app) => app.status === status).length;
      const pressed = selected.has(status);
      return always || count > 0 || pressed
        ? [{ key: status, label, count, pressed, tone: healthTone(status) }]
        : [];
    });
  });

  protected readonly visibleApps = computed(() => {
    const selected = this.selected();
    const matching =
      selected.size === 0
        ? this.searched()
        : this.searched().filter((app) => selected.has(app.status));
    // The API sends healthy cards first, so the default order is worked out here.
    switch (this.sort()) {
      case 'name':
        return [...matching].sort(byName);
      case 'rate':
        return [...matching].sort(byRate);
      default:
        return [...matching].sort(byWorst);
    }
  });

  constructor() {
    // Without a signed-in user there is nothing to ask the API for.
    const signInError = this.auth.signInError();
    if (signInError !== null) {
      this.errorKind.set('signin');
      this.error.set(signInError);
      this.loading.set(false);
      return;
    }

    // The explanation panel is optional: without the rules it is simply not shown.
    this.appsService
      .getHealthRules()
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((rules) => this.rules.set(rules));

    merge(interval(REFRESH_INTERVAL_MS).pipe(startWith(0)), this.manualRefresh)
      .pipe(
        switchMap(() => {
          this.loading.set(true);
          return this.appsService.getAppsHealth().pipe(
            catchError((response: unknown) => {
              this.showProblem(response);
              return of(null);
            }),
          );
        }),
        takeUntilDestroyed(inject(DestroyRef)),
      )
      .subscribe((apps) => {
        if (apps) {
          const now = new Date();
          this.apps.set(apps);
          this.error.set(null);
          this.lastUpdated.set(now);
          this.now.set(now);
        }
        this.loading.set(false);
      });

    // Loaded whichever tab is open, so a dead letter shows on the tab and in the page title wherever the reader is.
    merge(interval(REFRESH_INTERVAL_MS).pipe(startWith(0)), this.manualRefresh)
      .pipe(
        switchMap(() => {
          this.serviceBusLoading.set(true);
          return this.appsService.getServiceBus().pipe(
            catchError((response: unknown) => {
              this.showServiceBusProblem(response);
              return of(null);
            }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((report) => {
        if (report) {
          const now = new Date();
          this.serviceBus.set(report.entities);
          this.serviceBusRefreshSeconds.set(report.refreshSeconds);
          this.serviceBusError.set(null);
          this.serviceBusUpdated.set(now);
          this.now.set(now);
        }
        this.serviceBusLoading.set(false);
      });

    timer(CLOCK_INTERVAL_MS, CLOCK_INTERVAL_MS)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.now.set(new Date()));

    // A tab left open on a second screen shows the trouble in its title.
    effect(() => {
      const attention = this.unhealthyCount() + this.deadLetteredCount();
      this.title.setTitle(attention > 0 ? `(${attention}) ${PAGE_TITLE}` : PAGE_TITLE);
    });

    effect(() => {
      const view = {
        selected: this.selected(),
        query: this.query(),
        sort: this.sort(),
        view: this.view(),
        group: this.group(),
      };
      // The other tab has its own parameters in the address; these must not come back while it is open.
      if (this.tab() === 'application-insights') {
        writeViewToUrl(view);
      }
    });

    // The header is only on the page while the dashboard is, so it is looked up again whenever it appears.
    effect((onCleanup) => {
      const header = this.topbar()?.nativeElement;
      if (!header || typeof IntersectionObserver === 'undefined') {
        return;
      }
      const observer = new IntersectionObserver(([entry]) =>
        this.headerVisible.set(entry.isIntersecting),
      );
      observer.observe(header);
      onCleanup(() => {
        observer.disconnect();
        this.headerVisible.set(true);
      });
    });
  }

  protected refresh(): void {
    this.manualRefresh.next();
  }

  // 401 and 403 say what the reader can do about it; anything else is "the API cannot be reached".
  private showProblem(response: unknown): void {
    const status = response instanceof HttpErrorResponse ? response.status : 0;
    if (status === 401 || status === 403) {
      // What was loaded before is not this reader's to see any more.
      this.apps.set([]);
      this.rules.set(null);
      this.lastUpdated.set(null);
      this.serviceBus.set([]);
      this.serviceBusUpdated.set(null);
    }

    if (status === 401) {
      this.errorKind.set('rejected');
      this.error.set('The API did not accept your sign-in.');
    } else if (status === 403) {
      this.errorKind.set('forbidden');
      const who = this.auth.userName();
      this.error.set(
        `${who === '' ? 'Your account' : who} is not a member of the group that may use this dashboard. Ask the owner of the group for access, or sign in with another account.`,
      );
    } else {
      this.errorKind.set('unreachable');
      this.error.set('The dashboard could not reach the API. Check that the backend is running.');
    }
  }

  // A refused reader gets the gate from the health call; here only the Service Bus data is taken away.
  private showServiceBusProblem(response: unknown): void {
    const status = response instanceof HttpErrorResponse ? response.status : 0;
    if (status === 401 || status === 403) {
      this.serviceBus.set([]);
      this.serviceBusUpdated.set(null);
    }
    this.serviceBusError.set(
      status === 403
        ? 'You are not allowed to see the Service Bus data.'
        : 'The dashboard could not load the Service Bus data. Check that the backend is running.',
    );
  }

  // What the banner says about the cards below it, whichever tab shows it.
  private static bannerNote(updated: Date | null): string {
    return `${
      updated === null
        ? ''
        : `The cards below still show the data from ${formatDate(updated, 'mediumTime', 'en-US')}. `
    }We try again every minute.`;
  }

  protected readonly bannerNote = computed(() => App.bannerNote(this.lastUpdated()));
  protected readonly serviceBusBannerNote = computed(() =>
    App.bannerNote(this.serviceBusUpdated()),
  );

  protected toggleFilter(key: string): void {
    const status = key as HealthStatus;
    const next = new Set(this.selected());
    if (!next.delete(status)) {
      next.add(status);
    }
    this.selected.set(next);
  }

  protected changeSelect(change: { id: string; value: string }): void {
    if (change.id === 'group') {
      this.group.set(change.value);
    } else if (change.id === 'sort') {
      this.sort.set(change.value as SortKey);
    }
  }

  // The details pages read their filters from the address only once, so the overview sets them before it navigates.
  protected openFromOverview(target: { route: string; search: string }): void {
    if (target.route === '/application-insights') {
      this.query.set(target.search);
      this.selected.set(new Set());
      this.group.set('');
    } else if (target.route === '/service-bus') {
      this.serviceBusView.query.set(target.search);
      this.serviceBusView.selected.set(new Set());
      this.serviceBusView.namespace.set('');
    }
  }

  protected changeView(value: string): void {
    this.view.set(value as ViewMode);
  }

  protected clearFilter(): void {
    this.selected.set(new Set());
  }

  // Clears the search, the group and the health buttons; the sort order and the view stay.
  protected clearAll(): void {
    this.query.set('');
    this.group.set('');
    this.selected.set(new Set());
  }
}
